import type { Band } from "../shared/economy";

// Age-differentiated reinforcement copy, Summit edition.
//
// Grounding principles (deliberate, not decoration):
// - Praise effort and process, never ability or outcomes (growth mindset).
// - Goal-gradient: name the small remaining distance ("only 12 away").
// - Endowed progress: frame progress already made ("you're 60% up").
// - Bands D and E get autonomy-supportive language (self-determination
//   theory): respect, choice, ownership — nothing a teen would find cringe.
// - Misses are neutral and recoverable. Never shame a zero day.

export type MotivationEvent =
  | "save_below"
  | "save_almost"
  | "liftoff"          // kept as the internal event name; the moment is the SUMMIT
  | "surplus"
  | "streak_checkpoint"
  | "milestone"
  | "unlocked"
  | "redeem_sent"
  | "wish_sent"
  | "comeback";

interface MotivationContext {
  name: string;
  remaining: number;
  unit: string;
  streak: number;
  prizeName?: string;
  away?: number;
  currency: string;
  percentToPrize?: number;
}

type CopyBuilder = (context: MotivationContext) => string;

const COPY: Record<Band, Partial<Record<MotivationEvent, CopyBuilder[]>>> = {
  A: {
    save_below: [
      () => "Another night of reading together. That is how readers grow! ⭐",
      () => "You sat for the whole story. That was the job, and you did it!",
    ],
    liftoff: [
      () => "Reading night done! You earned your star! ⭐",
      ({ name }) => `Great sitting and listening, ${name}! Star earned! ⭐`,
    ],
    milestone: [
      ({ prizeName }) => `Your stars are getting close to: ${prizeName}!`,
    ],
    unlocked: [
      ({ prizeName }) => `You have enough stars for ${prizeName}! Tell Mom or Dad!`,
    ],
    redeem_sent: [() => "Your star wish went to Mom and Dad! 🌟"],
    wish_sent: [() => "Your wish is on its way to Mom and Dad! 🌟"],
    comeback: [() => "A fresh reading night, whenever you’re ready. ⭐"],
  },
  B: {
    save_below: [
      ({ remaining, unit }) => `Good climbing. ${remaining} more ${unit} and you reach the top!`,
      () => "Every lesson is another step up the mountain. Keep going!",
    ],
    save_almost: [
      ({ remaining, unit }) => `SO close to the top! Just ${remaining} ${unit} left!`,
    ],
    liftoff: [
      () => "Goal met! You climbed all the way up, and now the day is yours! 🏔️",
      ({ name }) => `${name}, you made it to the top! Free time unlocked!`,
    ],
    surplus: [
      () => "You explored past your goal! Your learning keeps growing.",
    ],
    streak_checkpoint: [
      ({ streak }) => `${streak} days of climbing in a row! Keep that steady climb going! 🔥`,
    ],
    milestone: [
      ({ prizeName, percentToPrize }) => `You're ${percentToPrize}% of the way to ${prizeName}! Keep climbing!`,
    ],
    unlocked: [
      ({ prizeName }) => `🎉 You saved up enough for ${prizeName}! You can claim it right now!`,
    ],
    redeem_sent: [() => "Request sent to your parent! They will check it soon. 🏔️"],
    wish_sent: [() => "Wish sent to your parent! 🌟"],
    comeback: [() => "Welcome back to the trail! Every climb starts with one good day."],
  },
  C: {
    save_below: [
      ({ remaining, unit }) => `Logged. ${remaining} ${unit} between you and the summit. You've got this.`,
      () => "That's real climbing in the log. The hard days build the most strength.",
    ],
    save_almost: [
      ({ remaining, unit }) => `${remaining} ${unit} from the top. One more push.`,
    ],
    liftoff: [
      () => "Goal met. Required work done, free time unlocked. Earned, not given. 🏔️",
      () => "Top of the mountain. The rest of the day belongs to you.",
    ],
    surplus: [
      () => "Past your goal. More practice helps your skills stick. Strong climb.",
    ],
    streak_checkpoint: [
      ({ streak }) => `${streak}-day climbing streak. Steady work is adding up. 🔥`,
    ],
    milestone: [
      ({ prizeName, away, currency }) => `Only ${away} ${currency} between you and ${prizeName}.`,
      ({ percentToPrize, prizeName }) => `${percentToPrize}% of the way to ${prizeName}. Keep stacking.`,
    ],
    unlocked: [
      ({ prizeName, currency }) => `Unlocked: ${prizeName}. Your ${currency}, your call. 🎉`,
    ],
    redeem_sent: [({ currency }) => `Request is in. ${currency} only leaves your pack when a parent approves.`],
    wish_sent: [() => "Wish logged. A parent will review it soon."],
    comeback: [() => "Back at the trailhead. Yesterday is gone; today's climb is what counts."],
  },
  D: {
    save_below: [
      ({ remaining, unit }) => `Logged. ${remaining} ${unit} to the top. Finish it and the day's yours.`,
      () => "In the log. Consistency is the whole game.",
    ],
    save_almost: [
      ({ remaining, unit }) => `${remaining} ${unit} left. Close it out.`,
    ],
    liftoff: [
      () => "Goal met. Free time unlocked. Nobody can un-earn that.",
      () => "Top reached. The rest of today is yours to run.",
    ],
    surplus: [
      () => "You practiced past your goal. Your learning keeps growing.",
    ],
    streak_checkpoint: [
      ({ streak }) => `${streak} straight days on the mountain. Keep climbing.`,
    ],
    milestone: [
      ({ away, currency, prizeName }) => `${away} ${currency} from ${prizeName}. Within reach.`,
    ],
    unlocked: [
      ({ prizeName }) => `${prizeName} is unlocked. You earned it; your move.`,
    ],
    redeem_sent: [() => "Request sent. It's on your parent's dashboard."],
    wish_sent: [() => "Wish submitted. It'll show up on your parent's dashboard."],
    comeback: [() => "Streak reset, strength didn't. Start the next climb today."],
  },
  E: {
    save_below: [
      ({ remaining, unit }) => `Logged. ${remaining} ${unit} remaining today.`,
    ],
    save_almost: [
      ({ remaining, unit }) => `${remaining} ${unit} left. Almost cleared.`,
    ],
    liftoff: [
      () => "Cleared. Your time is your own.",
      () => "Done for the day. That discipline compounds.",
    ],
    surplus: [
      () => "Past target. Surplus rate applied.",
    ],
    streak_checkpoint: [
      ({ streak }) => `${streak}-day run. Keep climbing.`,
    ],
    milestone: [
      ({ away, currency, prizeName }) => `${away} ${currency} from ${prizeName}.`,
    ],
    unlocked: [
      ({ prizeName }) => `${prizeName}: unlocked. Claim it whenever you want.`,
    ],
    redeem_sent: [() => "Request submitted."],
    wish_sent: [() => "Added to your wishlist queue."],
    comeback: [() => "New run starts today."],
  },
};

// Deterministic pick that varies day to day without Math.random, so the same
// event on the same day reads the same message.
function pick(options: CopyBuilder[], seedText: string): CopyBuilder | null {
  let seed = 0;
  for (const char of seedText) seed = (seed * 31 + char.charCodeAt(0)) % 997;
  return options[seed % options.length] ?? options[0] ?? null;
}

export function motivationLine(band: Band, event: MotivationEvent, context: MotivationContext, look?: 'adventure'|'studio'): string | null {
  if(look==='studio'){
    const c=context;
    const lines:Record<MotivationEvent,string>={save_below:`Saved. ${c.remaining} ${c.unit} left today.`,save_almost:`${c.remaining} ${c.unit} to finish today's goal.`,liftoff:'Goal met. Your time is yours.',surplus:'Your practice is saved. Your learning keeps growing.',streak_checkpoint:`${c.streak} goal days in your streak.`,milestone:`${c.away} ${c.currency} from ${c.prizeName}.`,unlocked:`You can request ${c.prizeName}.`,redeem_sent:'Request sent to your parent.',wish_sent:'Request saved for your parent to review.',comeback:'Your saved skills are ready when you are.'};
    return lines[event];
  }
  const options = COPY[band][event] ?? COPY.C[event];
  if (!options || options.length === 0) return null;
  const builder = pick(options, `${context.name}:${event}:${new Date().toDateString()}`);
  return builder ? builder(context) : null;
}

// Under-the-ring encouragement while today is still in progress.
export function progressLine(band: Band, percent: number, remaining: number, unit: string): string {
  if (percent >= 100) {
    return band === "A" ? "Star earned tonight! ⭐" : band === "E" ? "Cleared for today." : "Free time unlocked. Enjoy it, you earned it.";
  }
  if (percent === 0) {
    return band === "A" ? "Tonight's story is waiting!" : band === "D" || band === "E" ? "Log today's work when you're done." : "The trail's open. Today's climb starts whenever you do.";
  }
  if (percent >= 80) {
    return band === "A" ? "Almost story time done!" : `${remaining} ${unit} from the top. You're nearly there.`;
  }
  if (percent >= 50) {
    return `Past the halfway camp. ${remaining} ${unit} to go.`;
  }
  return band === "D" || band === "E" ? `${remaining} ${unit} remaining.` : `Good start up the mountain. ${remaining} ${unit} to the summit.`;
}
